'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import {
  ArrowLeft,
  Users,
  KeyRound,
  ShieldCheck,
  Plus,
  Mail,
  Phone,
  Trash2,
  Lock,
  Unlock,
  Check,
  UserCheck
} from 'lucide-react';
import { SettingsSidebar } from '@/components/dashboard/settings/SettingsSidebar';

interface StaffMember {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  roleTitle: string;
  avatarUrl: string | null;
  colorHex: string;
  active: boolean;
}

export default function TeamSettingsPage() {
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [counterPin, setCounterPin] = useState<string>('1234');
  const [hasPin, setHasPin] = useState<boolean>(true);
  const [isChangingPin, setIsChangingPin] = useState<boolean>(false);
  const [newPinInput, setNewPinInput] = useState<string>('');

  // Modal Nuevo Colaborador
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [newName, setNewName] = useState<string>('');
  const [newRole, setNewRole] = useState<string>('Profesional / Barbero');
  const [newEmail, setNewEmail] = useState<string>('');
  const [newPhone, setNewPhone] = useState<string>('');
  const [newColor, setNewColor] = useState<string>('#10b981');
  const [creating, setCreating] = useState<boolean>(false);

  const [statusMessage, setStatusMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  const fetchStaff = useCallback(async () => {
    try {
      const [resStaff, resBiz] = await Promise.all([
        fetch('/api/staff'),
        fetch('/api/business')
      ]);
      const [dataStaff, dataBiz] = await Promise.all([
        resStaff.json(),
        resBiz.json()
      ]);

      if (dataStaff.success) setStaff(dataStaff.staff || []);
      if (dataBiz.success && dataBiz.business) {
        setHasPin(Boolean(dataBiz.business.counterPin));
        if (dataBiz.business.counterPin) setCounterPin(dataBiz.business.counterPin);
      }
    } catch (e) {
      console.warn('Error fetching staff/pin:', e);
    }
  }, []);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  const handleSavePin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\d{4}$/.test(newPinInput)) {
      alert('El PIN debe tener exactamente 4 dígitos numéricos.');
      return;
    }

    try {
      const res = await fetch('/api/business', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ counterPin: newPinInput })
      });
      const data = await res.json();
      if (data.success) {
        setCounterPin(newPinInput);
        setHasPin(true);
        setIsChangingPin(false);
        setNewPinInput('');
        setStatusMessage({ text: 'PIN de mostrador actualizado con éxito.', type: 'success' });
      }
    } catch (err: any) {
      setStatusMessage({ text: 'Error al guardar PIN', type: 'error' });
    }
  };

  const handleCreateStaff = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);

    try {
      const res = await fetch('/api/staff', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newName.trim(),
          roleTitle: newRole,
          email: newEmail.trim() || undefined,
          phone: newPhone.trim() || undefined,
          colorHex: newColor,
          active: true
        })
      });
      const data = await res.json();
      if (data.success) {
        setStatusMessage({ text: `Colaborador ${newName} añadido con éxito.`, type: 'success' });
        setIsModalOpen(false);
        setNewName('');
        setNewEmail('');
        setNewPhone('');
        fetchStaff();
      }
    } catch (e: any) {
      setStatusMessage({ text: 'Error al crear colaborador', type: 'error' });
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-16">
      {/* Cabecera */}
      <div className="space-y-1">
        <Link
          href="/dashboard"
          className="inline-flex items-center gap-1 text-xs font-bold text-slate-400 hover:text-slate-600 transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Volver al inicio</span>
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
          Usuarios y permisos
        </h1>
        <p className="text-xs text-slate-500">
          Administrá el acceso a la plataforma, roles de tus colaboradores y seguridad de mostrador.
        </p>
      </div>

      {statusMessage && (
        <div
          className={`p-4 rounded-2xl text-xs font-semibold flex items-center justify-between shadow-2xs ${
            statusMessage.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
              : 'bg-rose-50 text-rose-800 border border-rose-200'
          }`}
        >
          <span>{statusMessage.text}</span>
          <button onClick={() => setStatusMessage(null)} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
      )}

      {/* Grid 2 Columnas */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start pt-2">
        {/* Sidebar */}
        <div className="lg:col-span-4">
          <SettingsSidebar currentPath="/dashboard/settings/team" />
        </div>

        {/* Contenido */}
        <div className="lg:col-span-8 space-y-6">
          {/* Tarjeta 1: PIN de Mostrador */}
          <div className="bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-8 shadow-sm space-y-4">
            <div className="flex items-center gap-3 pb-2 border-b border-slate-100">
              <div className="w-10 h-10 rounded-2xl bg-amber-50 text-amber-700 flex items-center justify-center font-bold">
                <KeyRound className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900">PIN de mostrador</h3>
                <p className="text-xs text-slate-500">Protegé la caja, ventas y métricas sensibles ante clientes curiosos.</p>
              </div>
            </div>

            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200/80 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-800">Estado del PIN:</span>
                  <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-800 rounded-full text-[10px] font-black uppercase tracking-wider">
                    {hasPin ? 'Activo (4 dígitos)' : 'Desactivado'}
                  </span>
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  Se solicita para ver la pestaña de Ventas o modificar precios desde el mostrador.
                </p>
              </div>

              <div>
                {isChangingPin ? (
                  <form onSubmit={handleSavePin} className="flex items-center gap-2">
                    <input
                      type="password"
                      maxLength={4}
                      autoFocus
                      required
                      placeholder="****"
                      value={newPinInput}
                      onChange={(e) => setNewPinInput(e.target.value.replace(/\D/g, ''))}
                      className="w-20 px-3 py-1.5 bg-white border border-emerald-500 rounded-xl text-center text-sm font-bold tracking-widest text-slate-900 focus:ring-2 focus:ring-emerald-500/20"
                    />
                    <button
                      type="submit"
                      className="px-3 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-xs font-bold transition cursor-pointer"
                    >
                      Guardar
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsChangingPin(false)}
                      className="px-2 py-1.5 text-xs text-slate-500 hover:text-slate-800 font-bold"
                    >
                      ✕
                    </button>
                  </form>
                ) : (
                  <button
                    type="button"
                    onClick={() => setIsChangingPin(true)}
                    className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-100 text-slate-800 rounded-xl text-xs font-bold shadow-2xs transition cursor-pointer"
                  >
                    Cambiar PIN
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Tarjeta 2: Equipo de Trabajo */}
          <div className="bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-8 shadow-sm space-y-6">
            <div className="flex items-center justify-between pb-2 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center font-bold">
                  <Users className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900">Equipo de trabajo ({staff.length})</h3>
                  <p className="text-xs text-slate-500">Colaboradores con agenda propia o acceso al sistema.</p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setIsModalOpen(true)}
                className="px-4 py-2 bg-[#0a3826] hover:bg-[#07291c] text-white rounded-xl text-xs font-bold shadow-sm transition inline-flex items-center gap-1.5 cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>+ Agregar colaborador</span>
              </button>
            </div>

            {/* Lista de Colaboradores */}
            <div className="divide-y divide-slate-100">
              {staff.map((s) => (
                <div key={s.id} className="py-3.5 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3.5">
                    <div
                      className="w-10 h-10 rounded-2xl flex items-center justify-center font-black text-white text-sm shadow-xs"
                      style={{ backgroundColor: s.colorHex || '#10b981' }}
                    >
                      {s.name.charAt(0)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="text-xs font-bold text-slate-900">{s.name}</h4>
                        {s.roleTitle && (
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-md text-[10px] font-semibold">
                            {s.roleTitle}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-[11px] text-slate-400 mt-0.5">
                        {s.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {s.email}</span>}
                        {s.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {s.phone}</span>}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 font-bold rounded-lg text-xs border border-emerald-200/60">
                      Activo
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tarjeta 3: Matriz de Permisos */}
          <div className="bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-8 shadow-sm space-y-4">
            <div className="flex items-center gap-3 pb-2 border-b border-slate-100">
              <div className="w-10 h-10 rounded-2xl bg-blue-50 text-blue-700 flex items-center justify-center font-bold">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900">Niveles de permiso por rol</h3>
                <p className="text-xs text-slate-500">Jerarquía y control de visibilidad en el negocio.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200/80 space-y-1.5">
                <span className="text-xs font-bold text-slate-900 block">Dueño / Admin</span>
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  Control total sobre facturación, credenciales de Mercado Pago, exportaciones y configuración.
                </p>
              </div>

              <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200/80 space-y-1.5">
                <span className="text-xs font-bold text-slate-900 block">Recepcionista</span>
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  Gestión integral de turnos en agenda, cobro en mostrador y CRM clientes. Sin acceso a cuentas bancarias.
                </p>
              </div>

              <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200/80 space-y-1.5">
                <span className="text-xs font-bold text-slate-900 block">Profesional / Staff</span>
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  Visualización de su propia agenda de turnos asignados y liquidación de comisiones personales.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Nuevo Colaborador */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-xs">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl space-y-5">
            <div className="flex items-center justify-between pb-2 border-b border-slate-100">
              <h3 className="text-sm font-bold text-slate-900">Nuevo colaborador</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-700 font-bold">
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateStaff} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Nombre completo *</label>
                <input
                  type="text"
                  required
                  placeholder="Ej: Marcos Albarracín"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-900"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Puesto / Especialidad</label>
                <input
                  type="text"
                  placeholder="Ej: Barbero Senior / Estilista"
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-900"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Email</label>
                  <input
                    type="email"
                    placeholder="marcos@..."
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Teléfono</label>
                  <input
                    type="tel"
                    placeholder="3462..."
                    value={newPhone}
                    onChange={(e) => setNewPhone(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Color en la agenda</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={newColor}
                    onChange={(e) => setNewColor(e.target.value)}
                    className="w-9 h-9 rounded-xl border border-slate-200 cursor-pointer p-0.5 bg-white"
                  />
                  <span className="text-xs font-mono font-bold text-slate-700">{newColor}</span>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-800"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="px-5 py-2.5 bg-[#0a3826] hover:bg-[#07291c] text-white rounded-xl text-xs font-bold transition shadow-xs disabled:opacity-50"
                >
                  {creating ? 'Guardando...' : 'Crear colaborador'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
